<template>
  <div>
    <DriverUserHealth active-type="user" />
    <div class="d-flex justify-content-between">
      <b-card class="user-details__card d-flex">
        <b-card-title>
          <h2>Personal Information</h2>
        </b-card-title>
        <b-row>
          <b-col
            xs="5"
          >
            <div class="d-flex">
              <b-avatar
                :src="pic"
                variant="light-danger"
                size="70px"
                rounded
                class="mr-2 mb-2"
              />
              <div class="d-flex flex-column">
                <h2>{{ details.firstName + ' ' + details.lastName }}</h2>
                <p class="mb-1">
                  {{ details.email }}
                </p>

              </div>
            </div>
            <div class="d-flex align-items-center">

              <b-badge
                pill
                :variant="`light-${currentState == 'ACTIVE' ? 'success' : 'danger'}`"
                class="p-1"
              >
                {{ currentState }}
              </b-badge>
              <div class="ml-1">
                <b-spinner
                  v-if="loadingInfo"
                  small
                  variant="primary"
                />
                <b-form-checkbox
                  v-else
                  v-model="switchState"
                  switch
                  class="custom-control-success"
                  @change="switchUserState"
                />
              </div>
            </div>
          </b-col>
          <b-col
            xs="7"
          >
            <table class="mt-2 mt-xl-0 w-100">
              <tr
                v-for="(item, i) in pickFrom"
                :key="i"
              >
                <th class="pb-50">
                  <feather-icon
                    :icon="item.icon"
                    class="mr-75"
                  />
                  <span class="font-weight-bold">{{ item.label }}</span>
                </th>
                <td class="pb-50">
                  {{ item.key }}
                </td>
              </tr>
              <tr>
                <th class="pb-50">
                  <feather-icon
                    icon="StarIcon"
                    class="mr-75"
                  />
                  <span class="font-weight-bold">Rating</span>
                </th>
                <td class="pb-50">
                  {{ rating }}
                </td>
              </tr>
              <tr>
                <th class="pb-50">
                  <feather-icon
                    icon="StarIcon"
                    class="mr-75"
                  />
                  <span class="font-weight-bold">Last OTP</span>
                </th>
                <td class="pb-50">
                  <b-button
                    variant="primary"
                    pill
                    size="sm"
                    :disabled="loadingOTP"
                    @click="getLastOTP"
                  >
                    Get OTP
                  </b-button>
                  <span v-if="userLastOTP"> / {{ userLastOTP }}</span>
                </td>
              </tr>
            </table>
          </b-col>
        </b-row>
      </b-card>
      <div class="last__completed__trip__card">
        <last-completed-trip
          user-type="user"
        />
      </div>
    </div>

    <b-card class="devices">
      <b-col md="12">
        <DeviceDetailsV2
          v-if="details"
          :user="details"
        />
      </b-col>
    </b-card>

    <b-card class="phones">
      <b-col md="12">
        <PhoneDetailsV2
          v-if="details"
          :user="details"
        />
      </b-col>
    </b-card>

  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BBadge, BCardTitle, BAvatar, BFormCheckbox, BSpinner, BButton,
} from 'bootstrap-vue'
import {
  ref,
} from '@vue/composition-api'
import formatDate from '@/utils/date-format'
import useDriverUserRating from '@/views/driver-user-shared/driver-user-reviews/hooks/useDriverUserReviews'
import useRegisterStoreModule from '@/hooks/useRegisterStoreModule'
import Rating from '@/views/driver-user-shared/driver-user-reviews/store'
import LastCompletedTrip from '@/views/driver-user-shared/last-completed-trip/LastCompletedTrip.vue'
import DeviceDetailsV2 from './DeviceDetailsV2.vue'
import PhoneDetailsV2 from './PhoneDetailsV2.vue'
import DriverUserHealth from '@/views/driver-user-shared/driver-user-trips/components/DriverUserHealth.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BBadge,
    BCardTitle,
    BAvatar,
    BButton,
    DriverUserHealth,
    BFormCheckbox,
    BSpinner,
    LastCompletedTrip,
    DeviceDetailsV2,
    PhoneDetailsV2,
  },
  props: {
    details: {
      type: Object,
      default: () => {},
    },
    pic: {
      type: String,
      default: '',
    },
  },
  setup(props, ctx) {
    useRegisterStoreModule('reviews', Rating)
    const loadingInfo = ref(false)
    const currentState = ref(props.details.activeState)
    const switchState = ref(currentState.value === 'ACTIVE')
    const { rating } = useDriverUserRating('users', props.details.externalId, ctx)
    const pickFrom = [
      {
        icon: 'MapPinIcon',
        label: 'Signup Location',
        key: props?.details?.metadata?.signup_location ? `${props?.details?.metadata?.signup_location?.latitude},${props?.details?.metadata?.signup_location?.longitude}` : 'N/A',
      },
      {
        icon: 'PhoneIcon',
        label: 'Phone Number',
        key: props.details.phoneNumber,
      },
      {
        icon: 'UserIcon',
        label: 'Gender',
        key: props.details.gender,
      },
      {
        icon: 'FeatherIcon',
        label: 'Created At',
        key: formatDate(props.details.createdAt),

      },
      {
        icon: 'FeatherIcon',
        label: 'Updated At',
        key: formatDate(props.details.updatedAt),
      },
    ]
    const deviceDetails = [
      {
        icon: 'HashIcon',
        label: 'Device ID',
        key: props.details.deviceId,
      },
      {
        icon: 'FeatherIcon',
        label: 'Application Version',
        key: props.details.applicationVersion,
      },
      {
        icon: 'FeatherIcon',
        label: 'Operating System',
        key: props.details.operatingSystem,
      },
    ]

    const changeUserStatus = async (currStatus, id) => {
      let changedStatus = null
      if (currStatus === 'ACTIVE') changedStatus = 'INACTIVE'
      if (currStatus === 'INACTIVE') changedStatus = 'ACTIVE'
      await ctx.root.$store.dispatch('users/updateUser', {
        userId: id,
        data: {
          active_state: changedStatus,
        },
      })
    }
    const switchUserState = async () => {
      loadingInfo.value = true
      await changeUserStatus(currentState.value, props.details.id)
      if (currentState.value === 'ACTIVE') {
        currentState.value = 'INACTIVE'
        switchState.value = false
      } else {
        currentState.value = 'ACTIVE'
        switchState.value = true
      }
      loadingInfo.value = false
    }

    const userLastOTP = ref(undefined)
    const loadingOTP = ref(false)
    const getLastOTP = async () => {
      loadingOTP.value = true
      const res = await ctx.root.$store.dispatch('users/fetchLastOtp', props.details.phoneNumber)
      const result = res?.data?.data?.data?.otp_code
      if (result) {
        userLastOTP.value = result
      } else {
        userLastOTP.value = 'Not Found'
      }
      loadingOTP.value = false
    }

    return {
      pickFrom,
      rating,
      deviceDetails,
      switchUserState,
      loadingInfo,
      currentState,
      switchState,
      userLastOTP,
      loadingOTP,
      getLastOTP,
    }
  },
}
</script>

<style>
.user-details__card,.device__card {
    width: 66%;
}
.last__completed__trip__card {
    width: 33%;
}
</style>
